import * as React from 'react'
import { Slot } from '@radix-ui/react-slot'
import { cva, type VariantProps } from 'class-variance-authority'
import { cn } from '@/lib/utils'

const buttonVariants = cva(
	'group relative inline-flex shrink-0 items-center justify-center overflow-hidden whitespace-nowrap rounded-xl text-sm font-semibold tracking-[-0.21px] transition-all duration-200 ease-in-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:pointer-events-none [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0',
	{
		variants: {
			variant: {
				primary: 'border border-primary-border hover:border-primary disabled:border-transparent',
				secondary: 'border border-border disabled:border-transparent',
				default: 'border border-primary-border hover:border-primary disabled:border-transparent',
				destructive: 'border border-destructive/40 hover:border-destructive disabled:border-transparent',
				outline: 'rounded-md border border-input bg-background hover:bg-accent hover:text-accent-foreground disabled:opacity-50',
				ghost: 'rounded-md border border-transparent hover:bg-accent hover:text-accent-foreground disabled:opacity-50',
				link: 'rounded-md border border-transparent text-primary underline-offset-4 hover:underline disabled:opacity-50',
			},
			size: {
				default: 'h-11 px-0 sm:h-[50px]',
				sm: 'h-9 px-0',
				lg: 'h-14 px-0',
				icon: 'h-12 w-12 px-0',
			},
		},
		defaultVariants: {
			variant: 'primary',
			size: 'default',
		},
	},
)

const brandedInnerVariants = cva(
	'relative flex h-full w-full items-center justify-center gap-2 overflow-hidden rounded-[10px] px-4 font-semibold tracking-[-0.21px] sm:px-6 sm:text-base sm:tracking-[-0.24px]',
	{
		variants: {
			variant: {
				primary: 'bg-primary text-background group-disabled:bg-border group-disabled:text-subtle-foreground',
				secondary:
					'bg-secondary text-muted-foreground hover:bg-[radial-gradient(circle_3rem_at_50%_150%_in_hsl_shorter_hue,transparent_0%,hsl(var(--secondary))_90%,hsl(var(--secondary))_100%),linear-gradient(90deg,hsl(var(--primary)/0.5),hsl(var(--primary)/0.5))] group-disabled:bg-border group-disabled:text-subtle-foreground',
				default: 'bg-primary text-background group-disabled:bg-border group-disabled:text-subtle-foreground',
				destructive: 'bg-destructive/10 text-destructive hover:bg-destructive/15 group-disabled:bg-border group-disabled:text-subtle-foreground',
			},
			size: {
				default: '',
				sm: 'px-3 text-sm sm:px-4 sm:text-sm',
				lg: 'px-6 text-base',
				icon: 'px-0',
			},
		},
		defaultVariants: {
			variant: 'primary',
			size: 'default',
		},
	},
)

type ButtonVariantProps = VariantProps<typeof buttonVariants>
type BrandedVariant = 'primary' | 'secondary' | 'default' | 'destructive'

function isBrandedVariant(variant: ButtonProps['variant']): variant is BrandedVariant {
	return variant === 'primary' || variant === 'secondary' || variant === 'default' || variant === 'destructive' || variant == null
}

function isPrimaryBranded(variant: BrandedVariant) {
	return variant === 'primary' || variant === 'default'
}

function navigatorClassName(variant: BrandedVariant) {
	if (isPrimaryBranded(variant)) {
		return 'bg-background/80 group-hover:bg-background group-disabled:bg-subtle-foreground'
	}
	if (variant === 'destructive') {
		return 'bg-destructive/70 group-hover:bg-destructive group-disabled:bg-subtle-foreground'
	}
	return 'bg-border group-hover:bg-primary group-hover:shadow-[0_0_6px_1px_hsl(var(--primary)/0.55)] group-disabled:bg-subtle-foreground group-disabled:shadow-none'
}

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement>, ButtonVariantProps {
	asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(({ className, variant = 'primary', size = 'default', asChild = false, children, ...props }, ref) => {
	if (asChild || !isBrandedVariant(variant)) {
		const Comp = asChild ? Slot : 'button'
		return (
			<Comp className={cn(buttonVariants({ variant, size }), className)} ref={ref} {...props}>
				{children}
			</Comp>
		)
	}

	const primary = isPrimaryBranded(variant)

	return (
		<button className={cn(buttonVariants({ variant, size }), className)} ref={ref} {...props}>
			<span className={cn(brandedInnerVariants({ variant, size }))}>
				{primary ? (
					<span
						aria-hidden
						className='pointer-events-none absolute inset-0 rounded-[10px] bg-[radial-gradient(ellipse_90%_85%_at_50%_45%,hsl(var(--primary-highlight))_0%,hsl(var(--primary)/0.42)_50%,hsl(var(--secondary))_100%)] opacity-0 shadow-[inset_0_0_20px_hsl(var(--secondary)/0.1),inset_0_-8px_16px_hsl(var(--secondary)/0.2)] transition-opacity duration-200 ease-in-out group-hover:opacity-70'
					/>
				) : null}
				<span className='relative z-10 inline-flex items-center justify-center gap-2'>{children}</span>
				<span
					className={cn(
						'absolute bottom-0.5 left-1/2 z-10 h-0.5 -translate-x-1/2 rounded-full transition-[width,background-color,box-shadow] duration-200 ease-in-out',
						size === 'icon' ? 'w-4 group-hover:w-5 group-disabled:w-4' : 'w-8 group-hover:w-10 group-disabled:w-8',
						navigatorClassName(variant),
					)}
					aria-hidden
				/>
			</span>
		</button>
	)
})
Button.displayName = 'Button'

export { Button, buttonVariants }
export type { ButtonVariantProps }
